import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import BackofficeLayout, { Card, Btn, fmtEUR, fmtPct } from "./BackofficeLayout";
import { api } from "../../lib/api";
import { toast, Toaster } from "sonner";
import { ArrowLeft, Plus, Trash2, HardHat, Landmark, FileSignature } from "lucide-react";

const EXP_CATS = [
  "travaux_gros_oeuvre", "travaux_vrd", "travaux_etudes",
  "travaux_finitions", "travaux_autres",
  "charges_generales", "frais_bancaires",
];

const emptyExp = { date: new Date().toISOString().slice(0, 10), vendor: "",
                   category: "travaux_gros_oeuvre", amount: 0, paid_by: "banque",
                   invoice_ref: "", notes: "" };

export default function ProjectDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [expForm, setExpForm] = useState(emptyExp);
  const [showExp, setShowExp] = useState(false);

  const load = () => api.get(`/backoffice/projects/${id}`).then((r) => setP(r.data));
  useEffect(() => { load(); }, [id]);

  const addExp = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/backoffice/projects/${id}/expenses`, {
        ...expForm, project_id: id,
        amount: parseFloat(expForm.amount) || 0,
      });
      toast.success("Dépense ajoutée");
      setExpForm(emptyExp); setShowExp(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  const delExp = async (eid) => {
    if (!window.confirm("Supprimer cette dépense ?")) return;
    await api.delete(`/backoffice/projects/expenses/${eid}`);
    load();
  };

  if (!p) return <BackofficeLayout title="Chargement…"><div className="text-slate-400">…</div></BackofficeLayout>;

  const k = p.kpi || {};

  return (
    <BackofficeLayout
      title={p.name}
      subtitle={`${p.entity_name} · ${p.location || "—"} ${p.surface_m2 ? `· ${p.surface_m2} m²` : ""}`}
      actions={
        <>
          <Link to="/backoffice-finance/projets"><Btn variant="ghost"><ArrowLeft className="w-3.5 h-3.5" /> Retour</Btn></Link>
          <Btn onClick={() => setShowExp((s) => !s)} data-testid="bof-exp-new-btn"><Plus className="w-3.5 h-3.5" /> Dépense</Btn>
        </>
      }
    >
      <Toaster theme="dark" position="top-right" />

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="bof-project-kpis">
        <MiniK label="Budget total" value={fmtEUR(p.budget_total)} />
        <MiniK label="Dépenses" value={fmtEUR(k.depenses_totales)} sub={fmtPct(k.avancement_financier) + " du budget"} />
        <MiniK label="Financement" value={fmtEUR(k.capital_bancaire_total)} sub={`Débloqué ${fmtEUR(k.capital_debloque)}`} />
        <MiniK label="Reste à tirer" value={fmtEUR(k.reste_a_tirer)} sub={fmtPct(k.debt_usage_ratio) + " utilisé"} />
        <MiniK label="Apport initial" value={fmtEUR(p.apport_initial)} sub={`Utilisé ${fmtEUR(k.apport_utilise)}`} />
        <MiniK label="Mensualité prêts" value={fmtEUR(k.mensualite_prets)} />
        <MiniK label="Loyer futur estimé" value={fmtEUR(k.loyer_futur_mensuel_estime)} />
        <MiniK label="Cash-flow futur / mois" value={fmtEUR(k.cash_flow_futur_mensuel_estime)}
               tone={k.cash_flow_futur_mensuel_estime >= 0 ? "green" : "red"} />
      </div>

      {/* Baux réels (si présents) */}
      {(k.nb_baux_actifs || 0) > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="bof-project-real-kpis">
          <MiniK label="Baux actifs" value={k.nb_baux_actifs} tone="green" />
          <MiniK label="Loyer réel / mois" value={fmtEUR(k.loyer_reel_mensuel)} tone="green"
                 sub="Depuis baux" />
          <MiniK label="Taxe foncière / mois" value={fmtEUR(k.taxe_fonciere_mensuelle)} tone="red" />
          <MiniK label="Cash-flow réel / mois" value={fmtEUR(k.cash_flow_reel_mensuel)}
                 tone={k.cash_flow_reel_mensuel >= 0 ? "green" : "red"}
                 sub="Loyer − TF − prêts" />
        </div>
      )}

      {/* Avancement bar */}
      <Card className="mb-6">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Avancement financier</div>
        <div className="h-3 bg-slate-800 rounded overflow-hidden">
          <div className="h-full bg-gradient-to-r from-amber-500 to-amber-300"
               style={{ width: `${Math.min(100, (k.avancement_financier || 0) * 100)}%` }} />
        </div>
        <div className="flex justify-between text-xs text-slate-500 mt-2">
          <span>{fmtEUR(k.depenses_totales)}</span>
          <span>{fmtEUR(p.budget_total)}</span>
        </div>
      </Card>

      {/* Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <Card>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-3">Répartition par catégorie</div>
          <BreakdownList data={k.by_category || {}} />
        </Card>
        <Card>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-3">Répartition par source de paiement</div>
          <BreakdownList data={k.by_paid_by || {}} />
        </Card>
      </div>

      {/* Baux liés au projet */}
      {(p.leases || []).length > 0 && (
        <Card className="mb-6" data-testid="bof-project-leases">
          <div className="flex items-center justify-between mb-3">
            <div className="font-bold text-white text-sm flex items-center gap-2">
              <FileSignature className="w-4 h-4 text-amber-400" /> Baux locatifs ({p.leases.length})
            </div>
            <Link to="/backoffice-finance/baux"><Btn variant="ghost">Gérer les baux</Btn></Link>
          </div>
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-[10px] uppercase tracking-widest text-slate-500">
              <tr className="border-b border-slate-800">
                <th className="py-2">Locataire</th>
                <th className="py-2">Statut</th>
                <th className="py-2 text-right">Loyer HT / mois</th>
                <th className="py-2 text-right">Charges</th>
                <th className="py-2 text-right">TF annuelle</th>
                <th className="py-2">Début</th>
              </tr>
            </thead>
            <tbody>
              {p.leases.map((lease) => (
                <tr key={lease.id} className="border-b border-slate-800/50">
                  <td className="py-2 font-bold">{lease.tenant_name}</td>
                  <td className="py-2 text-xs uppercase text-slate-400">{lease.status}</td>
                  <td className="py-2 font-mono text-right">{fmtEUR(lease.monthly_rent_ht)}</td>
                  <td className="py-2 font-mono text-right">{fmtEUR(lease.monthly_charges)}</td>
                  <td className="py-2 font-mono text-right">{fmtEUR(lease.taxe_fonciere_annual)}</td>
                  <td className="py-2 font-mono text-xs text-slate-400">{lease.start_date}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </Card>
      )}

      {/* Loans */}
      <Card className="mb-6" data-testid="bof-project-loans">
        <div className="flex items-center justify-between mb-3">
          <div className="font-bold text-white text-sm flex items-center gap-2">
            <Landmark className="w-4 h-4 text-amber-400" /> Prêts liés
          </div>
          <Link to="/backoffice-finance/prets"><Btn variant="ghost">Gérer les prêts</Btn></Link>
        </div>
        {(p.loans || []).length === 0 ? (
          <div className="text-sm text-slate-500">Aucun prêt attaché à ce projet.</div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-[10px] uppercase tracking-widest text-slate-500">
              <tr className="border-b border-slate-800">
                <th className="py-2">Prêteur</th>
                <th className="py-2">Capital</th>
                <th className="py-2">Taux</th>
                <th className="py-2">Mensualité</th>
                <th className="py-2">Débloqué</th>
              </tr>
            </thead>
            <tbody>
              {p.loans.map((l) => (
                <tr key={l.id} className="border-b border-slate-800/50">
                  <td className="py-2">{l.lender}</td>
                  <td className="py-2 font-mono">{fmtEUR(l.principal)}</td>
                  <td className="py-2 font-mono">{l.interest_rate}%</td>
                  <td className="py-2 font-mono">{fmtEUR(l.monthly_payment)}</td>
                  <td className="py-2 font-mono">{fmtEUR(l.disbursed_amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </Card>

      {/* Expense form */}
      {showExp && (
        <Card className="mb-6" data-testid="bof-exp-form">
          <form onSubmit={addExp} className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <input type="date" required value={expForm.date} onChange={(e) => setExpForm({ ...expForm, date: e.target.value })} className="input" />
            <input placeholder="Fournisseur" value={expForm.vendor} onChange={(e) => setExpForm({ ...expForm, vendor: e.target.value })} className="input" />
            <select value={expForm.category} onChange={(e) => setExpForm({ ...expForm, category: e.target.value })} className="input">
              {EXP_CATS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <input type="number" step="0.01" required placeholder="Montant" value={expForm.amount} onChange={(e) => setExpForm({ ...expForm, amount: e.target.value })} className="input" data-testid="bof-exp-amount" />
            <select value={expForm.paid_by} onChange={(e) => setExpForm({ ...expForm, paid_by: e.target.value })} className="input">
              <option value="banque">Banque</option>
              <option value="cash">Cash</option>
              <option value="apport">Apport</option>
              <option value="cca">Compte courant associé</option>
            </select>
            <input placeholder="N° facture" value={expForm.invoice_ref} onChange={(e) => setExpForm({ ...expForm, invoice_ref: e.target.value })} className="input" />
            <input placeholder="Notes" value={expForm.notes} onChange={(e) => setExpForm({ ...expForm, notes: e.target.value })} className="input md:col-span-2" />
            <div className="md:col-span-4 flex gap-2">
              <Btn type="submit" data-testid="bof-exp-save">Ajouter</Btn>
              <Btn type="button" variant="ghost" onClick={() => setShowExp(false)}>Annuler</Btn>
            </div>
          </form>
        </Card>
      )}

      {/* Expenses table */}
      <Card data-testid="bof-project-expenses">
        <div className="flex items-center justify-between mb-3">
          <div className="font-bold text-white text-sm flex items-center gap-2">
            <HardHat className="w-4 h-4 text-amber-400" /> Dépenses ({(p.expenses || []).length})
          </div>
        </div>
        {(p.expenses || []).length === 0 ? (
          <div className="text-sm text-slate-500 py-4 text-center">Aucune dépense enregistrée. Cliquez sur &quot;Dépense&quot; pour en ajouter.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-[10px] uppercase tracking-widest text-slate-500">
                <tr className="border-b border-slate-800">
                  <th className="py-2 pr-4">Date</th>
                  <th className="py-2 pr-4">Fournisseur</th>
                  <th className="py-2 pr-4">Catégorie</th>
                  <th className="py-2 pr-4">Source</th>
                  <th className="py-2 pr-4 text-right">Montant</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {p.expenses.map((e) => (
                  <tr key={e.id} className="border-b border-slate-800/50">
                    <td className="py-2 pr-4 font-mono text-xs text-slate-400">{e.date}</td>
                    <td className="py-2 pr-4">{e.vendor || "—"}</td>
                    <td className="py-2 pr-4 text-xs">{e.category}</td>
                    <td className="py-2 pr-4 text-xs">{e.paid_by}</td>
                    <td className="py-2 pr-4 text-right font-mono font-bold text-white">{fmtEUR(e.amount)}</td>
                    <td className="py-2">
                      <button onClick={() => delExp(e.id)} className="text-red-400 hover:text-red-300" data-testid={`bof-exp-delete-${e.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <style>{`
        .input { background: rgba(15, 23, 42, 0.6); border: 1px solid rgb(51, 65, 85); color: white;
                 padding: 0.5rem 0.75rem; border-radius: 0.375rem; width: 100%; font-size: 0.875rem; }
        .input:focus { outline: 2px solid rgb(251, 191, 36); outline-offset: -1px; }
      `}</style>
    </BackofficeLayout>
  );
}

const MiniK = ({ label, value, sub, tone = "amber" }) => {
  const tones = { amber: "text-amber-300", green: "text-emerald-300", red: "text-red-300", slate: "text-slate-200" };
  return (
    <div className="bg-slate-900/60 border border-slate-800 rounded-lg p-4">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <div className={`text-xl font-black mt-1.5 ${tones[tone]}`}>{value}</div>
      {sub && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
};

const BreakdownList = ({ data }) => {
  const total = Object.values(data).reduce((s, v) => s + v, 0);
  const entries = Object.entries(data).sort((a, b) => b[1] - a[1]);
  if (!entries.length) return <div className="text-sm text-slate-500">Aucune donnée.</div>;
  return (
    <div className="space-y-2">
      {entries.map(([k, v]) => (
        <div key={k}>
          <div className="flex justify-between text-xs mb-1">
            <span className="text-slate-300">{k}</span>
            <span className="font-mono text-white">{fmtEUR(v)}</span>
          </div>
          <div className="h-1 bg-slate-800 rounded overflow-hidden">
            <div className="h-full bg-amber-400" style={{ width: `${total > 0 ? (v / total) * 100 : 0}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
};
